import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, DateInput, FormField, Heading, Text, TextInput } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import StyledModal from "../components/StyledModel";
import { useReferenceData } from "../reference/ReferenceStore";
import { getStats, StatType, StatViewModel } from "../stats/StatsStore";
import { TeamViewModel } from "../teams/TeamsStore";
import { AddSeedMatchStatRequest, addStat, CreateSeedMatchRequest, SeedMatchStatViewModel, SeedMatchParticipantViewModel, SeedMatchViewModel, RemoveSeedMatchStatRequest, removeStat, updateSeedMatchStat, UpdateSeedMatchStatRequest, ParticipantStatSummary } from "./SeedMatchesStore";
import { Stat, StatColor, StatIcon } from "../stats/StatIcon";
import { RenderStat, StatValue } from "../stats/StatRenderer";
import { values } from "lodash";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

export const SeedMatchParticipantStats = ({ seedMatch, disabled, participant, round, handleChange, add }: { seedMatch: SeedMatchViewModel; disabled: boolean; participant: ParticipantStatSummary; round?: number; handleChange: Function; add: boolean }) => {
    const [showAddStatModal, setShowAddStatModal] = React.useState(false);
    const [showRemoveStatModal, setShowRemoveStatModal] = React.useState(false);

    const [statToRemove, setStatToRemove] = React.useState({} as SeedMatchStatViewModel);

    const [stat, setStat] = React.useState({} as StatViewModel);
    const [value, setValue] = React.useState(0);

    const prevGranularityRef = React.useRef(1);
    const [granularity, setGranularity] = React.useState(1);

    const [statCreateError, setStatCreateError] = React.useState("");
    const [statRemoveError, setStatRemoveError] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(false);

    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    const [rounds, setRounds] = React.useState([] as number[]);
    const [selectedRound, setSelectedRound] = React.useState(round || 1);

    React.useEffect(() => {
        getStats().then((response) => {
            setStatTypes(response.result);
        });
    }, []);

    React.useEffect(() => {
        setRounds(new Array(seedMatch.rounds).fill(0).map((x, i) => i + 1));
    }, [seedMatch]);

    React.useEffect(() => {
        setGranularity(1);
    }, [stat]);

    React.useEffect(() => {
        setValue((value * prevGranularityRef.current) / granularity);
        prevGranularityRef.current = granularity;
    }, [granularity]);

    const startAddStat = () => {
        setShowAddStatModal(true);
    };

    const addStatToSeedMatch = () => {
        setStatCreateError("");
        setIsLoading(true);
        addStat({ seedMatchID: seedMatch.id, participantID: participant.id, statID: stat.id, value: value * granularity, round: selectedRound } as AddSeedMatchStatRequest).then(async (response) => {
            if (response.success) {
                setStat({} as StatViewModel);
                setValue(0);
                handleChange();
                setShowAddStatModal(false);
            } else {
                setStatCreateError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const startRemoveStat = (stat: SeedMatchStatViewModel) => {
        setStatToRemove(stat);
        setShowRemoveStatModal(true);
    };

    const removeStatFromSeedMatch = () => {
        setStatRemoveError("");
        setIsLoading(true);
        removeStat({ seedMatchID: seedMatch.id, seedMatchStatID: statToRemove.id } as RemoveSeedMatchStatRequest).then((response) => {
            if (response.success) {
                handleChange();
                setShowRemoveStatModal(false);
                setShowUpdateStatModal(false);
            } else {
                setStatRemoveError(response.errors.join("\n"));
            }
            setIsLoading(false);
        });
    };

    const [showUpdateStatModal, setShowUpdateStatModal] = React.useState(false);
    const [statToUpdate, setStatToUpdate] = React.useState({} as SeedMatchStatViewModel);
    const [statUpdateError, setStatUpdateError] = React.useState("");

    const startUpdateStat = (stat: SeedMatchStatViewModel) => {
        setStatToUpdate(stat);
        setShowUpdateStatModal(true);
        setValue(stat.value);
    };

    const updateStat = () => {
        setStatUpdateError("");
        updateSeedMatchStat({
            seedMatchID: seedMatch.id,
            seedMatchStatID: statToUpdate.id,
            value: value * granularity,
        } as UpdateSeedMatchStatRequest).then((res) => {
            handleChange();
        });
    };

    return (
        <>
            {!add && (
                <Box>
                    {participant.stats["Round " + round] &&
                        participant.stats["Round " + round].map((seedMatchStat) => (
                            // <RenderStat key={participant.id + round + seedMatchStat.id} stat={seedMatchStat.stat} value={seedMatchStat.value} disabled={disabled} onClick={() => startUpdateStat(seedMatchStat)}></RenderStat>
                            <Box key={participant.id + round + seedMatchStat.id} hoverIndicator="background-front" onClick={() => startUpdateStat(seedMatchStat)} pad="xsmall" round="4px">
                                <Text>
                                    {StatIcon(seedMatchStat.stat)} {StatValue(seedMatchStat.stat, seedMatchStat.value)}
                                </Text>
                            </Box>
                        ))}
                </Box>
            )}
            {add && (
                <Box hoverIndicator="background-front" onClick={startAddStat} pad="xsmall" round="4px">
                    <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                </Box>
            )}

            <StyledModal isOpen={showAddStatModal} setIsOpen={setShowAddStatModal}>
                <Box width={"medium"}>
                    <Box direction="row" align="center" gap="small">
                        <img src={participant?.team?.logoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {participant?.team?.name}
                    </Box>
                    <Box gap="small" pad="small">
                        {statTypes.map((_stat, statTypeIndex) => (
                            <Button key={statTypeIndex} primary={stat.id == _stat.id} color={StatColor(_stat)} label={_stat.name} icon={StatIcon(_stat)} onClick={() => setStat(_stat)} disabled={isLoading}></Button>
                        ))}
                    </Box>
                    {stat.type == "Time" && (
                        <Box gap="small" pad="small" direction="row" wrap>
                            <Button margin={{ top: "small" }} primary={granularity == 1} label="Milliseconds" onClick={() => setGranularity(1)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 1000} label="Seconds" onClick={() => setGranularity(1000)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 60000} label="Minutes" onClick={() => setGranularity(60000)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 3600000} label="Hours" onClick={() => setGranularity(3600000)}></Button>
                        </Box>
                    )}
                    <FormField label="Value">
                        <TextInput type={"number"} value={value} onChange={(e) => setValue(+e.target.value)} />
                    </FormField>
                    <Box gap="small" pad="small" direction="row" wrap>
                        {rounds.map((r) => (
                            <Button margin={{ top: "small" }} key={r} primary={selectedRound == r} label={`Round ${r}`} onClick={() => setSelectedRound(r)}></Button>
                        ))}
                    </Box>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statCreateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowAddStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => addStatToSeedMatch()} label="Add" disabled={isLoading || !stat.id || !value}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showUpdateStatModal} setIsOpen={setShowUpdateStatModal}>
                <Box width={"medium"}>
                    <Heading size="small">{statToUpdate?.stat?.name}</Heading>
                    {statToUpdate.stat?.type == "Time" && (
                        <Box gap="small" pad="small" direction="row" wrap>
                            <Button margin={{ top: "small" }} primary={granularity == 1} label="Milliseconds" onClick={() => setGranularity(1)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 1000} label="Seconds" onClick={() => setGranularity(1000)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 60000} label="Minutes" onClick={() => setGranularity(60000)}></Button>
                            <Button margin={{ top: "small" }} primary={granularity == 3600000} label="Hours" onClick={() => setGranularity(3600000)}></Button>
                        </Box>
                    )}
                    <FormField label="Value">
                        <TextInput type={"number"} value={value} onChange={(e) => setValue(+e.target.value)} />
                    </FormField>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statUpdateError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowUpdateStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => updateStat()} label="Update" disabled={isLoading}></Button>
                    </Box>
                    <Button onClick={() => startRemoveStat(statToUpdate)} label="Remove Stat" color="status-critical" disabled={isLoading}></Button>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveStatModal} setIsOpen={setShowRemoveStatModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this stat?</Text>
                    <Text>
                        <pre style={{ whiteSpace: "pre-wrap" }}>{statRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveStatModal(false)} label="Cancel" disabled={isLoading}></Button>
                        <Button primary onClick={() => removeStatFromSeedMatch()} label="Remove" color="status-critical" disabled={isLoading}></Button>
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};

export default SeedMatchParticipantStats;
